import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { URL_MAIN } from "../rutas/rutas";

const DEV_BACKEND_FALLBACK =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1") &&
  window.location.port === "3000"
    ? "http://localhost:3005"
    : "";

// Usa la misma base API que el resto de CityFarm.
// Desarrollo: http://localhost:3005
// Producción: mismo dominio (cadena vacía).
const HISTORY_API_BASE = URL_MAIN || DEV_BACKEND_FALLBACK || "";

const API_HISTORY = `${HISTORY_API_BASE}/api/operation-history`;
const API_SUMMARY = `${HISTORY_API_BASE}/api/operation-history/summary`;
const API_READ = `${HISTORY_API_BASE}/api/operation-history/read`;
const API_HEALTH = `${HISTORY_API_BASE}/api/operation-history/health`;

const CATEGORY_FILTERS = [
  "ALL",
  "PRODUCTION",
  "AGRICULTURE",
  "MILL",
  "UPGRADES",
  "TRADE",
  "STAKE",
  "CONVERSION",
  "WITHDRAWALS",
  "PACKS",
  "OBJECTS",
  "VISITS",
  "MY_FARM",
];

const CATEGORY_ICONS = {
  PRODUCTION: "🌾",
  AGRICULTURE: "🚜",
  MILL: "⚙️",
  UPGRADES: "⬆️",
  TRADE: "🤝",
  STAKE: "🏦",
  CONVERSION: "🔄",
  WITHDRAWALS: "💸",
  PACKS: "📦",
  OBJECTS: "🎟️",
  VISITS: "🏡",
  ACCOUNT: "👤",
  OTHER: "📌",
};

const COPY = {
  es: {
    launcher: "HISTORIAL",
    title: "HISTORIAL DE OPERACIONES",
    subtitle: "Todos los movimientos de tu granja y economía",
    close: "CERRAR",
    refresh: "ACTUALIZAR",
    search: "Buscar jugador, recurso u operación…",
    empty: "No hay operaciones para este filtro.",
    loading: "Cargando historial…",
    error: "No se pudo cargar el historial.",
    recording: "REGISTRO ACTIVO",
    disconnected: "REGISTRO DESCONECTADO",
    previous: "ANTERIOR",
    next: "SIGUIENTE",
    page: "PÁGINA",
    of: "DE",
    gains: "Recibiste",
    costs: "Gastaste",
    balances: "Nuevo saldo",
    visitorPrefix: "Actividad en tu granja por",
    filters: {
      ALL: "TODOS",
      PRODUCTION: "PRODUCCIÓN",
      AGRICULTURE: "AGRICULTURA V2",
      MILL: "MOLINO",
      UPGRADES: "MEJORAS",
      TRADE: "TRADE",
      STAKE: "STAKE",
      CONVERSION: "CONVERSIÓN",
      WITHDRAWALS: "RETIROS",
      PACKS: "PACKS / SEMILLAS",
      OBJECTS: "OBJETOS",
      VISITS: "VISITAS",
      MY_FARM: "ACTIVIDAD EN MI GRANJA",
    },
    periods: {
      all: "TODO EL TIEMPO",
      today: "HOY",
      "7d": "7 DÍAS",
      "30d": "30 DÍAS",
    },
    actions: {
      COLLECT_WATER: "Recolectaste Water",
      COLLECT_ENERGY: "Recolectaste Energy",
      HARVEST_CROP: "Recolectaste un cultivo",
      HARVEST_WOOD: "Recolectaste Wood",
      HARVEST_ANIMAL: "Recolectaste producción animal",
      COLLECT_PRODUCTION: "Recolectaste producción",
      START_WATER: "Iniciaste producción de Water",
      START_ENERGY: "Iniciaste producción de Energy",
      PLANT_CROP: "Sembraste un cultivo",
      START_TREE: "Iniciaste producción de árbol",
      START_ANIMAL: "Iniciaste producción animal",
      START_PRODUCTION: "Iniciaste producción",
      AGRI_UNLOCK_PLOT: "Desbloqueaste una parcela",
      AGRI_HARVEST: "Cosechaste Agriculture V2",
      AGRI_PLANT: "Sembraste Agriculture V2",
      AGRI_SPECIALIZATION: "Aplicaste una especialización",
      AGRI_LEVEL_UP: "Subiste una parcela de nivel",
      AGRI_ACTION: "Operación de Agriculture V2",
      MILL_START: "Iniciaste un proceso del Molino",
      MILL_COMPLETE: "Completaste un proceso del Molino",
      MILL_REPAIR: "Reparaste el Molino",
      MILL_LEVEL_UP: "Subiste el Molino de nivel",
      UPGRADE_START: "Iniciaste una mejora",
      UPGRADE_COMPLETE: "Completaste una mejora",
      TRADE_SEND: "Enviaste un Trade",
      MARKET_SELL: "Creaste una orden de venta",
      MARKET_BUY: "Compraste en el mercado",
      MARKET_CANCEL: "Cancelaste una orden",
      STAKE_DEPOSIT: "Depositaste en Stake",
      STAKE_WITHDRAW: "Retiraste de Stake",
      TOKEN_CONVERSION: "Realizaste una conversión",
      WITHDRAWAL: "Realizaste un retiro",
      BUY_SEEDS: "Compraste semillas",
      BUY_PACK: "Compraste un Pack",
      USE_OBJECT: "Usaste un objeto",
      VISIT_WORK: "Trabajaste en otra granja",
      FARM_VISITOR_HARVEST: "Un jugador cosechó en tu granja",
      FARM_VISITOR_PLANT: "Un jugador sembró en tu granja",
      FARM_VISITOR_WATER: "Un jugador trabajó en tu pozo",
      FARM_VISITOR_ACTION: "Un jugador trabajó en tu granja",
      NPC_FARMER_CLAIM: "Reclamaste una recompensa NPC",
      OTHER_ACTION: "Operación de CityFarm",
    },
  },
  en: {
    launcher: "HISTORY",
    title: "OPERATION HISTORY",
    subtitle: "All movements from your farm and economy",
    close: "CLOSE",
    refresh: "REFRESH",
    search: "Search player, resource or operation…",
    empty: "No operations for this filter.",
    loading: "Loading history…",
    error: "History could not be loaded.",
    recording: "RECORDING ACTIVE",
    disconnected: "RECORDING DISCONNECTED",
    previous: "PREVIOUS",
    next: "NEXT",
    page: "PAGE",
    of: "OF",
    gains: "Received",
    costs: "Spent",
    balances: "New balance",
    visitorPrefix: "Activity on your farm by",
    filters: {
      ALL: "ALL",
      PRODUCTION: "PRODUCTION",
      AGRICULTURE: "AGRICULTURE V2",
      MILL: "MILL",
      UPGRADES: "UPGRADES",
      TRADE: "TRADE",
      STAKE: "STAKE",
      CONVERSION: "CONVERSION",
      WITHDRAWALS: "WITHDRAWALS",
      PACKS: "PACKS / SEEDS",
      OBJECTS: "OBJECTS",
      VISITS: "VISITS",
      MY_FARM: "ACTIVITY ON MY FARM",
    },
    periods: {
      all: "ALL TIME",
      today: "TODAY",
      "7d": "7 DAYS",
      "30d": "30 DAYS",
    },
    actions: {
      COLLECT_WATER: "Collected Water",
      COLLECT_ENERGY: "Collected Energy",
      HARVEST_CROP: "Harvested a crop",
      HARVEST_WOOD: "Collected Wood",
      HARVEST_ANIMAL: "Collected animal production",
      COLLECT_PRODUCTION: "Collected production",
      START_WATER: "Started Water production",
      START_ENERGY: "Started Energy production",
      PLANT_CROP: "Planted a crop",
      START_TREE: "Started tree production",
      START_ANIMAL: "Started animal production",
      START_PRODUCTION: "Started production",
      AGRI_UNLOCK_PLOT: "Unlocked a plot",
      AGRI_HARVEST: "Harvested Agriculture V2",
      AGRI_PLANT: "Planted Agriculture V2",
      AGRI_SPECIALIZATION: "Applied a specialization",
      AGRI_LEVEL_UP: "Upgraded a plot",
      AGRI_ACTION: "Agriculture V2 operation",
      MILL_START: "Started a Mill process",
      MILL_COMPLETE: "Completed a Mill process",
      MILL_REPAIR: "Repaired the Mill",
      MILL_LEVEL_UP: "Upgraded the Mill",
      UPGRADE_START: "Started an upgrade",
      UPGRADE_COMPLETE: "Completed an upgrade",
      TRADE_SEND: "Sent a Trade",
      MARKET_SELL: "Created a sell order",
      MARKET_BUY: "Bought from the market",
      MARKET_CANCEL: "Cancelled an order",
      STAKE_DEPOSIT: "Deposited into Stake",
      STAKE_WITHDRAW: "Withdrew from Stake",
      TOKEN_CONVERSION: "Completed a conversion",
      WITHDRAWAL: "Completed a withdrawal",
      BUY_SEEDS: "Bought seeds",
      BUY_PACK: "Bought a Pack",
      USE_OBJECT: "Used an object",
      VISIT_WORK: "Worked on another farm",
      FARM_VISITOR_HARVEST: "A player harvested on your farm",
      FARM_VISITOR_PLANT: "A player planted on your farm",
      FARM_VISITOR_WATER: "A player worked on your water well",
      FARM_VISITOR_ACTION: "A player worked on your farm",
      NPC_FARMER_CLAIM: "Claimed an NPC reward",
      OTHER_ACTION: "CityFarm operation",
    },
  },
};

const HISTORY_LANGUAGE_STORAGE_KEY = "cityfarm-v2-language-v6";

function normalizeHistoryLanguage(value) {
  return String(value || "").trim().toLowerCase() === "en" ? "en" : "es";
}

function readStoredHistoryLanguage() {
  if (typeof window === "undefined") return "";

  try {
    const value = window.localStorage?.getItem(HISTORY_LANGUAGE_STORAGE_KEY);
    return value === "en" || value === "es" ? value : "";
  } catch (_) {
    return "";
  }
}

function readCookie(name) {
  if (typeof document === "undefined") return "";

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length !== 2) return "";

  return decodeURIComponent(parts.pop().split(";").shift() || "");
}

function getToken() {
  if (typeof window === "undefined") return "";

  return (
    readCookie("token") ||
    window.sessionStorage?.getItem("token") ||
    window.localStorage?.getItem("token") ||
    ""
  );
}

async function apiRequest(url, options = {}) {
  const token = getToken();

  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token
        ? {
            token,
            Authorization: `Bearer ${token}`,
          }
        : {}),
      ...(options.headers || {}),
    },
  });

  let data = null;

  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }

  if (!response.ok) {
    throw new Error(data?.error || `HTTP ${response.status}`);
  }

  return data;
}

function formatNumber(value) {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) return "0";

  return number.toLocaleString(undefined, {
    maximumFractionDigits: 8,
  });
}

function formatUtc(value, language) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(date.getUTCFullYear()).slice(-2);
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const min = String(date.getUTCMinutes()).padStart(2, "0");

  return language === "es"
    ? `${dd}-${mm}-${yy} · ${hh}:${min} UTC`
    : `${mm}-${dd}-${yy} · ${hh}:${min} UTC`;
}

function humanizeAction(action) {
  return String(action || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function usefulMeta(meta) {
  const request = meta?.request || {};
  const keys = [
    "plotId",
    "landId",
    "tierraId",
    "id",
    "crop",
    "cultivo",
    "specialization",
    "especializacion",
    "item",
    "token",
    "cantidad",
    "amount",
    "semilla",
    "seeds",
  ];

  return keys
    .filter((key) => request?.[key] !== undefined)
    .slice(0, 5)
    .map((key) => ({
      key,
      value: request[key],
    }));
}

function HistoryRow({ row, language, copy }) {
  const positive = (row.changes || []).filter(
    (change) => Number(change.delta || 0) > 0
  );
  const negative = (row.changes || []).filter(
    (change) => Number(change.delta || 0) < 0
  );

  const actionTitle =
    copy.actions[row.action] || humanizeAction(row.action);

  const icon = CATEGORY_ICONS[row.category] || "📌";
  const incoming = row.scope === "farm_incoming";
  const metaRows = usefulMeta(row.meta);

  return (
    <article className={`cfhist-row ${incoming ? "is-incoming" : ""}`}>
      <div className="cfhist-row-icon">{icon}</div>

      <div className="cfhist-row-main">
        <div className="cfhist-row-top">
          <strong>{actionTitle}</strong>
          <time>{formatUtc(row.createdAt, language)}</time>
        </div>

        {incoming && (
          <div className="cfhist-visitor">
            🏡 {copy.visitorPrefix} <b>@{row.actor}</b>
          </div>
        )}

        {(positive.length > 0 || negative.length > 0) && (
          <div className="cfhist-flow">
            {positive.length > 0 && (
              <div>
                <span className="cfhist-flow-label positive">
                  + {copy.gains}
                </span>
                <p>
                  {positive
                    .map(
                      (change) =>
                        `${formatNumber(change.delta)} ${change.item}`
                    )
                    .join(" · ")}
                </p>
              </div>
            )}

            {negative.length > 0 && (
              <div>
                <span className="cfhist-flow-label negative">
                  − {copy.costs}
                </span>
                <p>
                  {negative
                    .map(
                      (change) =>
                        `${formatNumber(Math.abs(change.delta))} ${
                          change.item
                        }`
                    )
                    .join(" · ")}
                </p>
              </div>
            )}
          </div>
        )}

        {(row.changes || []).length > 0 && (
          <div className="cfhist-balances">
            <span>{copy.balances}:</span>
            {(row.changes || []).map((change) => (
              <b key={`${row.id}-${change.kind}-${change.item}`}>
                {change.item} {formatNumber(change.after)}
              </b>
            ))}
          </div>
        )}

        {metaRows.length > 0 && (
          <div className="cfhist-meta">
            {metaRows.map(({ key, value }) => (
              <span key={`${row.id}-${key}`}>
                {key}: {String(value)}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export default function CityFarmOperationHistory({ language }) {
  const [activeLanguage, setActiveLanguage] = useState(() =>
    normalizeHistoryLanguage(language || readStoredHistoryLanguage() || "es")
  );

  const lang = activeLanguage;
  const copy = COPY[lang];

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("ALL");
  const [period, setPeriod] = useState("30d");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState({
    items: [],
    total: 0,
    pages: 1,
  });

  const [summary, setSummary] = useState({
    unseen: 0,
    unseenFarm: 0,
    categories: {},
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [health, setHealth] = useState(null);

  const farmOnly = category === "MY_FARM";
  const apiCategory =
    category === "ALL" || category === "MY_FARM" ? "" : category;

  const badge = Number(summary.unseenFarm || summary.unseen || 0);

  // Sincroniza el idioma con CityFarm aunque el modal esté abierto.
  // Además consulta localStorage porque el selector ES/EN del mundo lo actualiza
  // en la misma pestaña y el evento `storage` no se dispara en ese caso.
  useEffect(() => {
    const next = normalizeHistoryLanguage(
      language || readStoredHistoryLanguage() || activeLanguage
    );
    setActiveLanguage((current) => (current === next ? current : next));
  }, [language]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncFromStorage = () => {
      const stored = readStoredHistoryLanguage();
      if (!stored) return;
      setActiveLanguage((current) =>
        current === stored ? current : normalizeHistoryLanguage(stored)
      );
    };

    syncFromStorage();
    const intervalId = window.setInterval(syncFromStorage, 250);
    window.addEventListener("storage", syncFromStorage);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("storage", syncFromStorage);
    };
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const result = await apiRequest(API_SUMMARY);

      if (result?.ok) {
        setSummary({
          unseen: Number(result.unseen || 0),
          unseenFarm: Number(result.unseenFarm || 0),
          categories: result.categories || {},
        });
      }
    } catch (_) {
      // El botón no debe romper el mundo si la API todavía no está desplegada.
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const result = await apiRequest(API_HEALTH);
      setHealth(result?.ok ? result : null);
    } catch (_) {
      setHealth(null);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    if (!open) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", "25");
      params.set("period", period);

      if (apiCategory) params.set("category", apiCategory);
      if (farmOnly) params.set("farmOnly", "1");
      if (search) params.set("q", search);

      const result = await apiRequest(
        `${API_HISTORY}?${params.toString()}`
      );

      setData({
        items: Array.isArray(result?.items) ? result.items : [],
        total: Number(result?.total || 0),
        pages: Math.max(1, Number(result?.pages || 1)),
      });
    } catch (requestError) {
      setError(requestError?.message || copy.error);
      setData({ items: [], total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [open, page, period, apiCategory, farmOnly, search, copy.error]);

  const markRead = useCallback(async () => {
    try {
      await apiRequest(API_READ, {
        method: "POST",
        body: JSON.stringify({ farmOnly: false }),
      });

      await loadSummary();
    } catch (_) {}
  }, [loadSummary]);

  useEffect(() => {
    loadSummary();
    const id = window.setInterval(loadSummary, 30000);
    return () => window.clearInterval(id);
  }, [loadSummary]);

  useEffect(() => {
    if (!open) return;

    loadHistory();
    loadHealth();
  }, [open, loadHistory, loadHealth]);

  useEffect(() => {
    if (!open) return;

    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    markRead();
  }, [open, markRead]);

  const filters = useMemo(
    () =>
      CATEGORY_FILTERS.map((key) => ({
        key,
        label: copy.filters[key],
        count:
          key === "ALL"
            ? Number(summary?.total || 0)
            : key === "MY_FARM"
              ? Number(summary?.unseenFarm || 0)
              : Number(summary?.categories?.[key] || 0),
      })),
    [copy.filters, summary]
  );

  const submitSearch = (event) => {
    event?.preventDefault?.();
    setPage(1);
    setSearch(searchDraft.trim());
  };

  const selectCategory = (next) => {
    setCategory(next);
    setPage(1);
  };

  return (
    <>
      <style>{`
        .cfhist-launcher {
          position: static;
          z-index: auto;
          transform: none;
          width: min(230px, 100%);
          min-width: 150px;
          height: 34px;
          border: 2px solid #ddb932;
          border-radius: 7px;
          background: linear-gradient(180deg, #183923, #102919);
          color: #fff1a7;
          font: 900 12px/1 Minecraft, monospace;
          letter-spacing: .03em;
          cursor: pointer;
          box-shadow: 0 2px 0 #06140a, inset 0 0 0 1px rgba(255,255,255,.08);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .cfhist-launcher:hover {
          filter: brightness(1.16);
        }

        .cfhist-launcher-badge {
          min-width: 19px;
          height: 19px;
          padding: 0 5px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          background: #c73e2f;
          color: #fff;
          border: 1px solid #ffb4a7;
          font-size: 9px;
        }

        .cfhist-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          background: rgba(1, 6, 3, .78);
          backdrop-filter: blur(5px);
          display: grid;
          place-items: center;
          padding: 18px;
        }

        .cfhist-modal {
          width: min(1280px, 97vw);
          height: min(880px, 94vh);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 3px solid #d9ad34;
          border-radius: 14px;
          position: relative;
          z-index: 1;
          isolation: isolate;
          opacity: 1;
          background: #0d331b;
          box-shadow: 0 30px 90px rgba(0,0,0,.82), inset 0 0 0 1px rgba(255,255,255,.035);
          color: #f4faef;
        }

        .cfhist-header {
          flex: 0 0 auto;
          min-height: 76px;
          padding: 13px 16px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 2px solid rgba(217,173,52,.48);
          background: #123e22;
        }

        .cfhist-header h2 {
          margin: 0 0 6px;
          color: #ffe991;
          font: 900 22px/1.2 Arial, sans-serif;
          letter-spacing: .02em;
          text-shadow: 1px 2px 0 rgba(0,0,0,.85);
        }

        .cfhist-header p {
          margin: 0;
          color: #ecf6ed;
          font: 700 15px/1.45 Arial, sans-serif;
        }

        .cfhist-health {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-height: 36px;
          padding: 0 13px;
          border-radius: 999px;
          border: 1px solid #5e9666;
          background: #0a2512;
          color: #a9f5b5;
          font: 900 13px/1.1 Arial, sans-serif;
          white-space: nowrap;
          text-shadow: 0 1px 0 #000;
        }

        .cfhist-health.is-off {
          border-color: #8a4d3b;
          color: #ffad98;
          background: #321912;
        }

        .cfhist-header-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 8px;
          margin-left: auto;
        }

        .cfhist-header-actions button,
        .cfhist-page button,
        .cfhist-refresh {
          border: 1px solid #789d70;
          border-radius: 7px;
          background: #173923;
          color: #ffffff;
          min-height: 38px;
          padding: 0 14px;
          font: 900 13px/1.1 Arial, sans-serif;
          cursor: pointer;
          text-shadow: 0 1px 0 rgba(0,0,0,.7);
        }

        .cfhist-header-actions button:last-child {
          border-color: #9b6339;
          background: #49291c;
        }

        .cfhist-controls {
          flex: 0 0 auto;
          overflow: visible;
          padding: 10px 12px;
          border-bottom: 1px solid rgba(217,173,52,.24);
          background: rgba(15, 44, 23, .92);
        }

        .cfhist-filters {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          overflow: visible;
          width: 100%;
          padding-bottom: 8px;
        }

        .cfhist-filter {
          flex: 0 0 auto;
          max-width: 100%;
          min-height: 38px;
          border: 1px solid rgba(151,195,143,.68);
          border-radius: 999px;
          padding: 0 14px;
          background: #173720;
          color: #ffffff;
          font: 900 12px/1.15 Arial, sans-serif;
          cursor: pointer;
          white-space: nowrap;
          letter-spacing: .01em;
          text-shadow: 0 1px 0 rgba(0,0,0,.75);
        }

        .cfhist-filter.is-active {
          border-color: #f2c33c;
          background: #5c5220;
          color: #fff0a5;
        }

        .cfhist-filter small {
          margin-left: 6px;
          color: #b8ebbf;
          font: 900 11px/1 Arial, sans-serif;
        }

        .cfhist-tools {
          display: grid;
          grid-template-columns: minmax(220px, 1fr) auto auto;
          align-items: stretch;
          gap: 8px;
          margin-top: 2px;
        }

        .cfhist-search {
          display: flex;
          gap: 6px;
        }

        .cfhist-search input,
        .cfhist-tools select {
          width: 100%;
          min-height: 40px;
          border: 1px solid #668d6e;
          border-radius: 7px;
          background: #081b0e;
          color: #ffffff;
          padding: 0 12px;
          outline: none;
          font: 600 14px/1 Arial, sans-serif;
        }

        .cfhist-search input::placeholder {
          color: #a9bbaa;
          opacity: 1;
        }

        .cfhist-tools select {
          min-width: 135px;
          font-weight: 800;
        }

        .cfhist-refresh {
          min-width: 64px;
        }

        .cfhist-search button {
          min-width: 46px;
          min-height: 40px;
          border: 1px solid #e4bd45;
          border-radius: 7px;
          background: #2d4e29;
          color: #fff1a7;
          font: 900 17px/1 Arial, sans-serif;
          cursor: pointer;
        }

        .cfhist-list {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 12px;
          display: grid;
          align-content: start;
          gap: 9px;
          scrollbar-color: #668c63 #112819;
        }

        .cfhist-row {
          display: grid;
          grid-template-columns: 48px 1fr;
          gap: 13px;
          padding: 15px 16px;
          border: 1px solid rgba(134,181,128,.48);
          border-radius: 11px;
          background: #143d22;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.025);
        }

        .cfhist-row.is-incoming {
          border-color: rgba(223,183,56,.76);
          box-shadow: inset 3px 0 0 #d9ad34;
        }

        .cfhist-row-icon {
          width: 44px;
          height: 44px;
          border-radius: 9px;
          display: grid;
          place-items: center;
          background: #092010;
          border: 1px solid rgba(238,193,57,.52);
          font-size: 23px;
          box-shadow: 0 2px 8px rgba(0,0,0,.28);
        }

        .cfhist-row-main {
          min-width: 0;
        }

        .cfhist-row-top {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: flex-start;
        }

        .cfhist-row-top strong {
          color: #ffe783;
          font: 800 16px/1.35 Arial, sans-serif;
          letter-spacing: .01em;
          text-shadow: 0 1px 0 rgba(0,0,0,.55);
        }

        .cfhist-row-top time {
          color: #c5dfc8;
          white-space: nowrap;
          font: 700 12px/1.3 Arial, sans-serif;
          padding-top: 2px;
        }

        .cfhist-visitor {
          margin-top: 6px;
          color: #f0d57c;
          font: 12px/1.3 Arial, sans-serif;
        }

        .cfhist-flow {
          margin-top: 10px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px 22px;
          font: 700 15px/1.45 Arial, sans-serif;
        }

        .cfhist-flow p {
          display: inline;
          margin: 0 0 0 7px;
          color: #ffffff;
        }

        .cfhist-flow-label {
          font-weight: 900;
        }

        .cfhist-flow-label.positive {
          color: #8ef0a0;
        }

        .cfhist-flow-label.negative {
          color: #ff9f8d;
        }

        .cfhist-balances {
          display: flex;
          flex-wrap: wrap;
          gap: 7px 10px;
          align-items: center;
          margin-top: 11px;
          padding-top: 10px;
          border-top: 1px dashed rgba(255,255,255,.20);
          color: #d0e5d2;
          font: 700 13px/1.4 Arial, sans-serif;
        }

        .cfhist-balances > span {
          color: #c8dccb;
          font-weight: 800;
        }

        .cfhist-balances b {
          border: 1px solid rgba(151,197,148,.4);
          border-radius: 6px;
          background: rgba(0,0,0,.22);
          padding: 5px 8px;
          color: #ffffff;
          font-size: 13px;
        }

        .cfhist-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-top: 7px;
        }

        .cfhist-meta span {
          border-radius: 999px;
          background: rgba(217,173,52,.12);
          border: 1px solid rgba(217,173,52,.34);
          padding: 5px 8px;
          color: #f0e7b8;
          font: 700 11px/1.1 Arial, sans-serif;
        }

        .cfhist-status {
          margin: auto;
          padding: 34px;
          color: #bfd5c2;
          text-align: center;
          font: 14px/1.5 Arial, sans-serif;
        }

        .cfhist-status.is-error {
          color: #ffac98;
        }

        .cfhist-page {
          flex: 0 0 auto;
          min-height: 58px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 14px;
          border-top: 1px solid rgba(217,173,52,.32);
          background: #0a2111;
          color: #ffffff;
          font: 900 13px/1.1 Arial, sans-serif;
        }

        .cfhist-page > span {
          min-width: 120px;
          text-align: center;
          letter-spacing: .02em;
        }

        .cfhist-page button:disabled {
          opacity: .35;
          cursor: default;
        }

        @media (max-width: 980px) {
          .cfhist-header {
            align-items: flex-start;
          }

          .cfhist-header-actions {
            width: 100%;
            justify-content: flex-start;
            margin-left: 0;
          }

          .cfhist-tools {
            grid-template-columns: 1fr 150px auto;
          }
        }

        @media (max-width: 1320px) {
          .cfhist-launcher {
            min-width: 110px;
            width: 100%;
            font-size: 10px;
          }
        }

        /* PHASE31.1: ventana siempre delante y sólida */
        .cfhist-overlay {
          z-index: 2147483000 !important;
          background: rgba(0, 12, 8, .90) !important;
          backdrop-filter: blur(6px) !important;
        }

        .cfhist-modal,
        .cfhist-header,
        .cfhist-controls,
        .cfhist-list,
        .cfhist-page {
          opacity: 1 !important;
        }

        .cfhist-modal {
          background: #0d331b !important;
          box-shadow: 0 28px 100px rgba(0,0,0,.84) !important;
        }

        .cfhist-header,
        .cfhist-controls,
        .cfhist-page {
          background: #092714 !important;
        }

        .cfhist-list {
          background: #0b2d18 !important;
        }

        @media (max-width: 760px) {
          .cfhist-overlay {
            padding: 0;
          }

          .cfhist-modal {
            width: 100vw;
            height: 100dvh;
            max-height: none;
            border-radius: 0;
            border-left: 0;
            border-right: 0;
          }

          .cfhist-header {
            min-height: 66px;
            padding: 10px;
          }

          .cfhist-header p {
            display: none;
          }

          .cfhist-tools {
            grid-template-columns: 1fr;
          }

          .cfhist-row {
            grid-template-columns: 34px 1fr;
          }

          .cfhist-row-icon {
            width: 32px;
            height: 32px;
            font-size: 17px;
          }

          .cfhist-row-top {
            display: block;
          }

          .cfhist-row-top time {
            display: block;
            margin-top: 4px;
          }

          .cfhist-launcher {
            width: 100%;
            min-width: 42px;
            height: 32px;
            font-size: 0;
          }

          .cfhist-launcher::before {
            content: "📜";
            font-size: 15px;
          }
        }
      `}</style>

      <button
        type="button"
        className="cfhist-launcher"
        onClick={() => {
          setOpen(true);
          setPage(1);
        }}
        title={copy.launcher}
        aria-label={copy.launcher}
      >
        📜 {copy.launcher}
        {badge > 0 && (
          <span className="cfhist-launcher-badge">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>

      {open && typeof document !== "undefined" && createPortal((
        <div
          className="cfhist-overlay"
          onPointerDown={(event) => {
            event.stopPropagation();
            if (event.target === event.currentTarget) {
              setOpen(false);
            }
          }}
          onPointerMove={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
        >
          <section
            className="cfhist-modal"
            role="dialog"
            aria-modal="true"
            aria-label={copy.title}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <header className="cfhist-header">
              <div>
                <h2>📜 {copy.title}</h2>
                <p>{copy.subtitle}</p>
              </div>

              <div className="cfhist-header-actions">
                <span className={`cfhist-health ${health?.ok ? "" : "is-off"}`}>
                  ● {health?.ok ? copy.recording : copy.disconnected}
                  {health?.ok && Number(health.total || 0) > 0 ? ` · ${health.total}` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    loadHistory();
                    loadSummary();
                    loadHealth();
                  }}
                >
                  ↻ {copy.refresh}
                </button>

                <button type="button" onClick={() => setOpen(false)}>
                  ✕ {copy.close}
                </button>
              </div>
            </header>

            <div className="cfhist-controls">
              <div className="cfhist-filters">
                {filters.map((filter) => (
                  <button
                    type="button"
                    key={filter.key}
                    className={`cfhist-filter ${
                      category === filter.key ? "is-active" : ""
                    }`}
                    onClick={() => selectCategory(filter.key)}
                  >
                    {copy.filters[filter.key]}
                    {filter.key === "MY_FARM" &&
                      Number(summary.unseenFarm || 0) > 0 && (
                        <small>{summary.unseenFarm}</small>
                      )}
                  </button>
                ))}
              </div>

              <div className="cfhist-tools">
                <form className="cfhist-search" onSubmit={submitSearch}>
                  <input
                    value={searchDraft}
                    onChange={(event) => setSearchDraft(event.target.value)}
                    placeholder={copy.search}
                    maxLength={60}
                  />
                  <button type="submit">⌕</button>
                </form>

                <select
                  value={period}
                  onChange={(event) => {
                    setPeriod(event.target.value);
                    setPage(1);
                  }}
                >
                  {Object.entries(copy.periods).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="cfhist-refresh"
                  onClick={() => {
                    setSearchDraft("");
                    setSearch("");
                    setCategory("ALL");
                    setPeriod("30d");
                    setPage(1);
                  }}
                >
                  RESET
                </button>
              </div>
            </div>

            <div className="cfhist-list">
              {loading && (
                <div className="cfhist-status">{copy.loading}</div>
              )}

              {!loading && error && (
                <div className="cfhist-status is-error">
                  {copy.error}
                  <br />
                  <small>{error}</small>
                </div>
              )}

              {!loading &&
                !error &&
                data.items.length === 0 && (
                  <div className="cfhist-status">{copy.empty}</div>
                )}

              {!loading &&
                !error &&
                data.items.map((row) => (
                  <HistoryRow
                    key={row.id}
                    row={row}
                    language={lang}
                    copy={copy}
                  />
                ))}
            </div>

            <footer className="cfhist-page">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                ← {copy.previous}
              </button>

              <span>
                {copy.page} {page} {copy.of} {data.pages}
              </span>

              <button
                type="button"
                disabled={page >= data.pages}
                onClick={() =>
                  setPage((current) => Math.min(data.pages, current + 1))
                }
              >
                {copy.next} →
              </button>
            </footer>
          </section>
        </div>
      ), document.body)}
    </>
  );
}

